import React, { useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, Button } from "../components/UI";
import { Truck, Sparkles, MapPin, Phone, Navigation, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function isoToday() { return new Date().toISOString().slice(0, 10); }

export default function DeliveryRoute() {
  const [date, setDate] = useState(isoToday());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const optimize = async () => {
    setLoading(true); setData(null);
    try {
      const r = await api.post("/deliveries/optimize-route", { date });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-5xl" data-testid="delivery-route-page">
      <PageHeader
        title="Delivery Route Optimizer"
        subtitle="AI-powered route planning for today's delivery vehicle · cluster by city, prioritize heavy loads"
      />

      <div className="card-flat p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="route-controls">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-1">Dispatch Date</div>
          <input data-testid="route-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-base w-auto"/>
        </div>
        <Button onClick={optimize} disabled={loading} data-testid="optimize-btn">
          <Sparkles size={14}/> {loading ? "Optimizing…" : "AI Optimize Route"}
        </Button>
      </div>

      {loading && (
        <div className="card-flat p-10 text-center text-[#475569] flex flex-col items-center gap-2">
          <RefreshCw className="animate-spin text-[#C1121F]" size={32}/>
          <div className="text-sm">Gemini AI is planning the delivery route…</div>
        </div>
      )}

      {!loading && data && (
        <div className="space-y-4" data-testid="route-result">
          <div className="p-4 bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-[#C1121F]"/>
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#C1121F]">
                {data.ai_used ? "AI Optimized" : "Smart Heuristic"}
              </span>
              <span className="text-[10px] text-[#94A3B8] ml-auto">{data.total_stops} stops</span>
            </div>
            <div className="text-sm text-[#0F172A]" data-testid="route-narrative">{data.narrative}</div>
          </div>

          {data.total_stops === 0 ? (
            <div className="card-flat p-10 text-center">
              <Truck size={36} className="mx-auto mb-2 text-[#94A3B8] opacity-50"/>
              <div className="text-sm text-[#475569] mb-2">No deliveries planned for this date.</div>
              <div className="text-xs text-[#94A3B8]">
                Create Delivery Challans with planned_dispatch_date matching this date.
                <br/>Visit <a href="/pending-orders" className="text-[#C1121F] underline font-semibold">Pending Deliveries</a> to create DCs.
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="stops-list">
              {data.optimized.map((stop) => (
                <div key={stop.dc_id} className="card-flat p-3 flex items-center gap-3" data-testid={`stop-${stop.stop_number}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C1121F] text-white flex items-center justify-center font-bold font-mono-num">
                    {stop.stop_number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-sm">{stop.customer_name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded font-mono-num">{stop.dc_no}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-mono-num font-bold">{stop.total_qty} pcs</span>
                    </div>
                    <div className="text-xs text-[#475569] mt-0.5 flex items-center gap-1">
                      <MapPin size={10}/>{stop.city || "—"}{stop.address ? ` · ${stop.address}` : ""}
                    </div>
                    <div className="text-[10px] text-[#94A3B8] mt-0.5">
                      Order: {stop.order_no}
                      {stop.vehicle_no && <span> · 🚚 {stop.vehicle_no}</span>}
                      {stop.driver_name && <span> · 👤 {stop.driver_name}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {stop.maps_url && (
                      <a href={stop.maps_url} target="_blank" rel="noreferrer" className="p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100" title="Open in Maps">
                        <Navigation size={13}/>
                      </a>
                    )}
                    {stop.phone && (
                      <a href={`tel:${stop.phone}`} className="p-1.5 rounded bg-green-50 text-green-700 hover:bg-green-100" title="Call">
                        <Phone size={13}/>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="secondary" onClick={optimize} data-testid="reoptimize-btn">
              <RefreshCw size={12}/> Re-optimize
            </Button>
          </div>
        </div>
      )}

      {!loading && !data && (
        <div className="card-flat p-10 text-center text-[#94A3B8]">
          <Truck size={40} className="mx-auto mb-3 opacity-50"/>
          <div className="text-sm">Pick a dispatch date and click "AI Optimize Route" to get the best delivery sequence.</div>
        </div>
      )}
    </div>
  );
}
